import React, { useEffect, useState } from "react";
import { Club } from "api/clubs";
import { Button, Card, CardActions, CardContent, CardHeader, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, LinearProgress, Typography } from "@material-ui/core";
import { getAvailableSubscriptons, getCompanySubscriptons, getSubscriptonsSingle, Subscription, SubscriptionDetail, SubscriptionPlan, deleteSubscription, patchExtendSubscription, patchPreviewSubscription, patchCompanySubscription } from "api/company";
import { useSnackbar } from "notistack";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import config from "config";
import moment from "moment";
import { NumberPicker } from "components";
import SubscriptionDetailDialog from "./SubscriptionDetailDialog";
import StripeCardInput from "./StripeCardInput";
import AvailableSubscriptionList from "./AvailableSubscriptionList";
import { formatter, isActiveSubscription } from "../../../../utils/helpers";
import i18n from "../i18n";
import "../clubStyle.scss";

const stripePromise = loadStripe(config.stripe.publishableKey);

// prop types
interface SubscriptionTabProps {
    club: Club,
}

const SubscriptionTab = ({ club }: SubscriptionTabProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    const displayCustomError = (error: any) => enqueueSnackbar(
        `${i18n.insufficientNumberOfPurchasedSeatsMsg(error.associated)}`,
        { variant: "error" },
    );

    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const [isLoading, setLoading] = useState(false);
    const [subscriptionDetail, setSubscriptionDetail] = useState<SubscriptionDetail | null>(null);
    const [showSubscriptionDetailDialog, setShowSubscriptionDetailDialog] = useState(false);
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    const [showSelectedPlanDialog, setShowSelectedPlanDialog] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
    const [noOfSeat, setNoOfSeat] = useState(0);
    const [previewDescrptionMsg, setPreviewDescrptionMsg] = useState("");
    const [immediateTotalMsg, setImmediateTotalMsg] = useState("");
    const [nextPaymentMsg, setNextPaymentMsg] = useState("");
    const [showPreviewPopup, setShowPreviewPopup] = useState(false);
    const [showPreviewConfirm, setShowPreviewConfirm] = useState(false);
    const [showDialog, setShowDialog] = useState(false);

    // fetch available subscriptions
    const fetchAvailableSubscriptons = async (id: string) => {
        setPlans(await getAvailableSubscriptons({
            clubId: id,
        }));
    };

    useEffect(() => {
        if (club) {
            getSubscriptions();
        }
    }, [club]);

    const getSubscriptions = () => {
        getSubscriptionDetail(club.id).then((data) => {
            setSubscriptionDetail(data);
            fetchAvailableSubscriptons(club.id);
        });
    };

    const getSubscriptionDetail = async (clubId: string): Promise<SubscriptionDetail | null> => {
        try {
            setLoading(true);
            const subscriptions: Subscription[] = await getCompanySubscriptons({ clubId });
            if (subscriptions.length === 0) {
                return null;
            }
            const subscription = subscriptions[0];

            const singleSubscription = await getSubscriptonsSingle({ subscriptionId: subscription.id });
            return {
                id: subscription.id,
                subscriptionType: subscription.subscriptionType,
                subscriptionStatus: subscription.subscriptionStatus,
                numberOfSeats: subscription.numberOfSeats,
                stripePriceId: subscription.stripePriceId,
                pricePerSeat: singleSubscription.price.unit_amount,
                totalPrice: subscription.numberOfSeats * singleSubscription.price.unit_amount,
                renewalDate: singleSubscription.subscriptionInfos.current_period_end,
                subscriptionInfos: singleSubscription.subscriptionInfos,
                upcomingInvoice: singleSubscription.upcomingInvoice,
                price: singleSubscription.price,
                card: singleSubscription?.card,
                sepaDebit: singleSubscription?.sepaDebit,
                latestInvoice: singleSubscription.latestInvoice,
            };
        } catch (error) {
            displayError(error);
            return null;
        } finally {
            setLoading(false);
        }
    };

    const cancelSubscription = async (id: string) => {
        try {
            setLoading(true);
            await deleteSubscription({ subscriptionId: id });
            enqueueSnackbar(i18n.cancelSubscriptionSuccessMsg, { variant: "success" });
            setShowDeleteAlert(false);
            getSubscriptions();
        } catch (error) {
            displayError(error);
        } finally {
            setLoading(false);
        }
    };

    const extendSubscription = async (id: string) => {
        try {
            await patchExtendSubscription({ subscriptionId: id });
            enqueueSnackbar(i18n.extendSubscriptionSuccessMsg, { variant: "success" });
            getSubscriptions();
        } catch (error) {
            displayError(error);
        }
    };

    const previewSubscription = async () => {
        try {
            if (subscriptionDetail && selectedPlan) {
                setLoading(true);
                const previewResponse = await patchPreviewSubscription({ subscriptionId: subscriptionDetail.id, stripePriceId: selectedPlan.stripePriceId, numberOfSeats: noOfSeat });

                const { upcomingInvoice } = previewResponse;
                const immediateTotal = previewResponse.immediateTotalCents;
                const nextInvoiceTotal = previewResponse.nextInvoiceTotalCents;
                const newQuantity = noOfSeat;
                const currentQuantity = subscriptionDetail.numberOfSeats;

                let description = "";
                let nextPayment = "";
                let immediateTotalText = "";

                if (newQuantity >= currentQuantity) {
                    description += `${i18n.confirmDescriptionForSeatAdd({ newSeat: (newQuantity - currentQuantity), totalSeat: newQuantity, plan: selectedPlan?.title })}`;
                } else {
                    description += `${i18n.confirmDescriptionForSeatRemove({ newSeat: (currentQuantity - newQuantity), totalSeat: newQuantity, plan: selectedPlan?.title })}`;
                }

                if (immediateTotal > 0) {
                    immediateTotalText = `${i18n.confirmImmediateTotalTextGreaterThanZero({ immediateTotal: formatter.format(immediateTotal / 100) })}`;
                    nextPayment = `${i18n.confirmnextPaymentTextGreaterThanZero({ nextInvoiceTotal: formatter.format(nextInvoiceTotal / 100), upcomingInvoice: moment.unix(upcomingInvoice.created).format("DD.MM.YYYY") })}`;
                } else {
                    immediateTotalText = `${i18n.confirmImmediateTotalTextLessThanZero({ immediateTotal: formatter.format(immediateTotal / 100), nextInvoiceTotal: formatter.format(nextInvoiceTotal / 100), upcomingInvoice: moment.unix(upcomingInvoice.created).format("DD.MM.YYYY") })}`;
                    nextPayment = "";
                }

                setPreviewDescrptionMsg(description);
                setImmediateTotalMsg(immediateTotalText);
                setNextPaymentMsg(nextPayment);
                setShowPreviewPopup(false);
                setShowPreviewConfirm(true);
                setShowSelectedPlanDialog(false);
            }
        } catch (error: any) {
            if (error.type === "insufficientNumberOfPurchasedSeats") {
                displayCustomError(error);
            } else {
                displayError(error);
            }
        } finally {
            setLoading(false);
        }
    };

    const updateSubscription = async () => {
        try {
            setLoading(true);
            if (subscriptionDetail && selectedPlan) {
                await patchCompanySubscription({ subscriptionId: subscriptionDetail.id, stripePriceId: selectedPlan?.stripePriceId, numberOfSeats: noOfSeat });
                setShowPreviewConfirm(false);
                setShowSelectedPlanDialog(false);
                getSubscriptions();
                enqueueSnackbar(i18n.updateSubscriptionSuccessMsg, { variant: "success" });
            }
        } catch (error) {
            displayError(error);
        } finally {
            setLoading(false);
        }
    };

    const getCurrentPlan = (detail: SubscriptionDetail): SubscriptionPlan => plans.filter((p) => p.stripePriceId === detail.stripePriceId)[0] || { title: "Plan Not Found" };

    const selectPlan = (plan: SubscriptionPlan) => {
        if (club.clubStatus === "demo") {
            setShowDialog(true);
        } else {
            if (subscriptionDetail?.stripePriceId) {
                setNoOfSeat(subscriptionDetail.numberOfSeats);
            } else {
                setNoOfSeat(0);
            }
            setSelectedPlan(plan);
            setShowSelectedPlanDialog(true);
        }
    };

    const onChange = (seat: number) => {
        setNoOfSeat(seat);
    };

    // render meta row
    const renderMetaRow = (label: string, content: React.ReactNode) => (
        <>
            <Grid item xs={6}>
                <Typography>{label}</Typography>
            </Grid>
            <Grid item xs={6}>
                {content}
            </Grid>
        </>
    );

    return (
        <>
            <div key={club.id}>
                <Grid container spacing={2} style={{ textAlign: "center" }}>
                    <Grid item xs={12}>
                        {isLoading && <LinearProgress />}
                    </Grid>
                </Grid>
                <Grid container direction="column" alignItems="center" className="subTabMargin15">
                    {/* Current Plan */}
                    {subscriptionDetail && !showPreviewPopup && (
                        <Card variant="outlined" className="subscriptionCard">
                            <CardHeader
                                title={(
                                    <Typography variant="h4">{`${club.clubName}`}</Typography>
                                )}
                                subheader={(
                                    <Typography color="textSecondary" variant="body1">
                                        {i18n.monthlySubscription}
                                    </Typography>
                                )}
                            />
                            <CardContent>
                                <hr />
                                <Grid container spacing={1} className="subTabMargin10">
                                    {plans.length > 0 && (
                                        renderMetaRow(i18n.currentPlan, (
                                            <Typography>{getCurrentPlan(subscriptionDetail).title}</Typography>
                                        ))
                                    )}
                                    {renderMetaRow(i18n.subscriptionStatus, (
                                        isActiveSubscription(subscriptionDetail) ? (
                                            <Typography>{i18n.subscriptionStatuses[subscriptionDetail.subscriptionStatus]}</Typography>
                                        ) : (
                                            <Typography>{i18n.cancelled}</Typography>
                                        )
                                    ))}
                                    {renderMetaRow(i18n.noOfseat, (
                                        <Typography>{subscriptionDetail.numberOfSeats}</Typography>
                                    ))}
                                    {renderMetaRow(i18n.pricePerSeat, (
                                        <Typography>{formatter.format(subscriptionDetail.pricePerSeat / 100)}</Typography>
                                    ))}
                                    {renderMetaRow(i18n.totalPrice, (
                                        <Typography>{formatter.format(subscriptionDetail.totalPrice / 100)}</Typography>
                                    ))}
                                    {renderMetaRow(i18n.renewalDate, (
                                        <Typography>{moment.unix(subscriptionDetail.renewalDate).format("DD.MM.YYYY")}</Typography>
                                    ))}
                                </Grid>
                            </CardContent>
                            <CardActions className="subCardAction">
                                <Button size="small" variant="contained" color="secondary" onClick={() => setShowDeleteAlert(true)} disabled={subscriptionDetail.subscriptionInfos?.cancel_at_period_end && subscriptionDetail.subscriptionInfos.status === "active"}>{i18n.cancelSubscriptionBtn}</Button>
                                <Button size="small" variant="contained" color="primary" onClick={() => setShowPreviewPopup(true)}>{i18n.changePlan}</Button>
                                <Button size="small" variant="contained" color="default" onClick={() => setShowSubscriptionDetailDialog(true)}>{i18n.planDetails}</Button>
                                <Button size="small" variant="contained" color="primary" onClick={() => extendSubscription(subscriptionDetail.id)} disabled={isActiveSubscription(subscriptionDetail)}>{i18n.reActivateSubscription}</Button>
                            </CardActions>
                        </Card>
                    )}

                    {/* Available Plans */}
                    {((subscriptionDetail == null && plans.length > 0) || showPreviewPopup) && (
                        <AvailableSubscriptionList plans={plans} stripePriceId={subscriptionDetail?.stripePriceId} selectPlan={selectPlan} back={setShowPreviewPopup} />
                    )}
                </Grid>
            </div>

            {/* subscription detail dialog */}
            {subscriptionDetail && showSubscriptionDetailDialog && (
                <SubscriptionDetailDialog
                    onClose={() => setShowSubscriptionDetailDialog(false)}
                    subscriptionDetail={subscriptionDetail}
                    show={showSubscriptionDetailDialog}
                />
            )}

            {/* delete alert dialog */}
            <Dialog open={showDeleteAlert} onClose={() => setShowDeleteAlert(false)}>
                <DialogTitle>{i18n.areYouSure}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{i18n.cancelSubsccriptionMsg}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button color="secondary" onClick={() => setShowDeleteAlert(false)}>{i18n.cancel}</Button>
                    {(subscriptionDetail && <Button color="primary" onClick={() => cancelSubscription(subscriptionDetail.id)}>{i18n.cancelSubscriptionBtn}</Button>)}
                </DialogActions>
            </Dialog>

            {/* select plan dialog */}
            <Dialog open={showSelectedPlanDialog} onClose={() => setShowSelectedPlanDialog(false)} fullWidth>
                <DialogTitle>{selectedPlan?.title}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            {isLoading && <LinearProgress />}
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <NumberPicker
                                label={i18n.noOfseat}
                                step={1}
                                min={0}
                                max={1000}
                                value={noOfSeat}
                                onChange={onChange}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            {selectedPlan && (
                                <>
                                    <Typography variant="inherit">{`${i18n.monthlyTotalAmount} `}</Typography>
                                    <Typography variant="inherit">{formatter.format(selectedPlan.priceCentsPerSeat * noOfSeat / 100)}</Typography>
                                </>
                            )}
                        </Grid>
                    </Grid>
                    {!subscriptionDetail?.stripePriceId && (
                        <Elements stripe={stripePromise} options={{ appearance: { theme: "stripe" } }}>
                            {selectedPlan && (
                                <StripeCardInput plan={selectedPlan} noOfSeat={noOfSeat} club={club} closeMe={setShowSelectedPlanDialog} showSubscription={getSubscriptions} showLoader={setLoading} />
                            )}
                        </Elements>
                    )}
                </DialogContent>
                {subscriptionDetail && selectedPlan && subscriptionDetail?.stripePriceId && (
                    <DialogActions>
                        <Button color="secondary" onClick={() => setShowSelectedPlanDialog(false)}>{i18n.cancel}</Button>
                        {(subscriptionDetail?.stripePriceId && <Button color="primary" disabled={noOfSeat === 0 || (noOfSeat === subscriptionDetail.numberOfSeats && selectedPlan.stripePriceId === subscriptionDetail.stripePriceId)} onClick={() => { previewSubscription(); }}>{i18n.extendSubscriptionBtn}</Button>)}
                    </DialogActions>
                )}
            </Dialog>

            {/* Preview Confirm Alert */}
            <Dialog open={showPreviewConfirm} onClose={() => setShowPreviewConfirm(false)}>
                <DialogTitle>{i18n.updateSubscriptionConfirmMsg}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            {isLoading && <LinearProgress />}
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography>{previewDescrptionMsg}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography>{immediateTotalMsg}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography>{nextPaymentMsg}</Typography>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button color="secondary" onClick={() => setShowPreviewConfirm(false)}>{i18n.cancel}</Button>
                    {(subscriptionDetail?.stripePriceId && <Button color="primary" onClick={() => updateSubscription()}>{i18n.updateSubscriptionConfirm}</Button>)}
                </DialogActions>
            </Dialog>

            {/* Dialog for displaying demo mode message */}
            <Dialog open={showDialog} onClose={() => setShowDialog(false)}>
                <DialogTitle>{i18n.notice}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography>{i18n.demoModeMsg}</Typography>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button color="secondary" onClick={() => setShowDialog(false)}>{i18n.close}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default SubscriptionTab;
